import { useMemo } from 'react'
import { FormFieldConfig, useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { usePricingEditorDomainContext } from 'common/drawers/PricingEditor/communication'
import { PricingEditorReducerState } from 'common/drawers/PricingEditor/domain'

type UseExternalLedgerForm = () => {
  fieldsConfig: {
    integrations: (FormFieldConfig<Record<string, string>> & {
      id: string
      label: string
      options: { value: string; label: string }[]
    })[]
  }
}

export const defaultLedgerAccountValues = (
  existingValues: Record<string, string>,
  ledgerIntegrations: Record<
    string,
    { id: string; defaultLedgerAccount?: string }
  >
) => {
  return Object.values(ledgerIntegrations).reduce((acc, integration) => {
    if (existingValues[integration.id]) {
      return {
        ...acc,
        [integration.id]: existingValues[integration.id]
      }
    }

    return {
      ...acc,
      [integration.id]: integration.defaultLedgerAccount
    }
  }, {})
}

export const useExternalLedgerForm: UseExternalLedgerForm = () => {
  const pricingEditorContext = usePricingEditorDomainContext()
  const presetValues =
    pricingEditorContext.queries.rawData.data.pricingEditorData.common
      .integrationIds
  const valuesWithDefaults = defaultLedgerAccountValues(
    presetValues,
    pricingEditorContext.queries.rawData.data.integrations
  )

  const { fields } = useForm<
    PricingEditorReducerState['data']['pricingEditorData']['common']['integrationIds']
  >({
    value: valuesWithDefaults,
    showValidationErrors:
      pricingEditorContext.queries.rawData.editor.showValidationErrors,
    fieldConfiguration: Object.values(
      pricingEditorContext.queries.rawData.data.integrations
    ).map(integration => ({
      property: integration.id,
      options: integration.options.map(option => ({
        value: option.code,
        label: `${option.name} (${option.code})`
      })),
      validation: [required]
    })),
    disabled:
      !pricingEditorContext.queries.availableFeatures.common.form.available
        .enabled,
    /**
     * Sync the data back to the domain
     */
    onValidationStateChange: isValid => {
      pricingEditorContext.mutators.common.updateEditor({
        externalLedgersValid: isValid
      })
    },
    onChange: newData => {
      pricingEditorContext.mutators.common.updatePricingEditorData({
        common: {
          integrationIds: newData
        }
      })
    }
  })

  const enhancedFieldsConfig = useMemo(() => {
    return Object.entries(fields).map(([integrationId, fieldConfig]) => ({
      ...fieldConfig,
      id: integrationId,
      label: `${integrationId} ledger account`
    }))
  }, [fields])

  return {
    fieldsConfig: {
      integrations: enhancedFieldsConfig
    }
  }
}
